import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, useEffect } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuditLogs, type AuditLog } from '@/hooks/useAuditLogs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader } from '@/components/ui/loader';
import { PageHeader } from '@/components/layout/PageHeader';
import { FileTextIcon, HomeIcon, BuildingIcon } from 'lucide-react';
import { AuditFilters } from '@/components/audit/AuditFilters';
import { AuditLogsTable } from '@/components/audit/AuditLogsTable';
import { FeatureGate } from '@/components/features/FeatureGate';
import { loadAuthGuard } from '@/lib/auth-helpers';

export const Route = createFileRoute('/_authenticated/org/$orgId/audit')({
  loader: loadAuthGuard<{ orgId: string }>(async ({ params, context, ensureQueryData, sessionReady }) => {
    const { orgId } = params;
    const { trpc } = context;

    if (!sessionReady) {
      return { orgId };
    }
    
    // Preload organizations to get organizationId from slug
    const orgsData = await ensureQueryData(
      trpc.organizations.getAll.queryOptions(undefined),
    );
    
    // Find organization by slug
    const orgs = (orgsData as { organizations?: Array<{ id: string; slug: string }> })?.organizations || [];
    const organization = orgs.find((o) => o.slug === orgId || o.id === orgId);
    const organizationId = organization?.id;
    
    // Preload audit logs if we have organizationId
    // Note: Filtering is done client-side, so we preload all logs
    if (organizationId) {
      await ensureQueryData(
        trpc.audit.getByOrganization.queryOptions({ organizationId }),
      );
    }
    
    return { orgId };
  }),
  component: OrganizationAuditPage,
});

function OrganizationAuditPage() {
  const { orgId } = Route.useParams();
  const { organization, isLoading } = useOrganization(orgId);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAction, setSelectedAction] = useState<string>('all');
  const [selectedResourceType, setSelectedResourceType] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const { data: logsData, isLoading: logsLoading, error: logsError } = useAuditLogs(organization?.slug || orgId);
  const logs: AuditLog[] = logsData?.logs || [];

  const filteredLogs = useMemo(() => {
    return logs.filter((log: AuditLog) => {
      const matchesQuery = searchQuery
        ? (log.action?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           log.resource?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           log.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           log.user?.email?.toLowerCase().includes(searchQuery.toLowerCase()))
        : true;

      const matchesAction = selectedAction === 'all' ? true : log.action === selectedAction;
      const matchesResourceType = selectedResourceType === 'all' ? true : log.resourceType === selectedResourceType;
      const matchesSeverity = selectedSeverity === 'all' ? true : log.severity === selectedSeverity;
      const matchesStatus = selectedStatus === 'all' ? true : log.status === selectedStatus;

      return matchesQuery && matchesAction && matchesResourceType && matchesSeverity && matchesStatus;
    });
  }, [logs, searchQuery, selectedAction, selectedResourceType, selectedSeverity, selectedStatus]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedAction('all');
    setSelectedResourceType('all');
    setSelectedSeverity('all');
    setSelectedStatus('all');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedAction, selectedResourceType, selectedSeverity, selectedStatus]);

  if (isLoading || logsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader variant="spinner" size="lg" color="accent" text="Loading audit logs..." />
      </div>
    );
  }

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: 'Audit',
      to: `/org/${organization?.slug || orgId}/audit`,
    },
  ];

  return (
    <FeatureGate
      organizationId={orgId}
      featureKey="auditLogs"
      featureName="Audit Logs"
      description="Audit logs provide a complete record of all activities in your organization. Upgrade your plan to access this feature."
    >
      <div className="space-y-8">
        <PageHeader
          breadcrumbs={breadcrumbs}
          title="Audit Logs"
          subtitle={organization ? `Track all activities in ${organization.name}` : undefined}
        />

        <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
          <FileTextIcon className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{logs.length}</div>
          <p className="text-xs text-muted-foreground">Total events</p>
        </CardContent>
      </Card>

      <AuditFilters
        logs={logs}
        searchQuery={searchQuery}
        selectedAction={selectedAction}
        selectedResourceType={selectedResourceType}
        selectedSeverity={selectedSeverity}
        selectedStatus={selectedStatus}
        onSearchChange={setSearchQuery}
        onActionChange={setSelectedAction}
        onResourceTypeChange={setSelectedResourceType}
        onSeverityChange={setSelectedSeverity}
        onStatusChange={setSelectedStatus}
        onClearFilters={handleClearFilters}
        filteredCount={filteredLogs.length}
      />

      <AuditLogsTable
        logs={filteredLogs}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
      </div>
    </FeatureGate>
  );
}

